import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/api";
import { todayLocalISO } from "../utils/datetime";
import { getKategoriFromKode } from "../utils/productCode";
import { useAppSettings } from "../settings/AppSettingsProvider";
import {
  AlertCircle,
  ArrowRight,
  RefreshCcw,
  Package,
  ShoppingCart,
  ShieldCheck,
  AlertOctagon,
  TrendingUp,
  Activity,
  ArrowLeftRight,
  PieChart
} from "lucide-react";

const DONUT_CATEGORY_OPTIONS = [
  { value: "ALL", label: "Semua Kategori" },
  { value: "PRODUK UTAMA", label: "Produk Utama" },
  { value: "ADDITIONAL ITEM", label: "Additional Item" },
  { value: "SOUVENIR DAN PACKAGING", label: "Souvenir & Packaging" },
];

function Dashboard() {
  const { settings } = useAppSettings();
  const lowStockThreshold = settings?.lowStockThreshold ?? 5;

  const [ordersCount, setOrdersCount] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [donutCategory, setDonutCategory] = useState("ALL");

  const safeInt = (n, fallback = 0) => {
    const v = Math.trunc(Number(n));
    return Number.isFinite(v) ? v : fallback;
  };

  const threshold = safeInt(lowStockThreshold, 5);
  const safeThreshold = threshold > 0 ? threshold : 5;

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/dashboard/summary?lowStockThreshold=${encodeURIComponent(safeThreshold)}`);
      const data = res.data || {};
      setSummary(data);
      setOrdersCount(data.ordersCount ?? null);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Gagal mengambil data dashboard");
      setOrdersCount(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeThreshold]);

  const stockCounts = useMemo(() => {
    return summary?.stockCounts || { aman: 0, menipis: 0, habis: 0 };
  }, [summary]);

  const totalProduk = Number(summary?.totalProduk) || 0;

  const donutTotal = useMemo(() => {
    if (donutCategory === "ALL") return totalProduk;
    return Number(summary?.categoryCounts?.[donutCategory]?.total) || 0;
  }, [summary, donutCategory, totalProduk]);

  const donutStockCounts = useMemo(() => {
    if (donutCategory === "ALL") return stockCounts;
    return summary?.categoryCounts?.[donutCategory] || { aman: 0, menipis: 0, habis: 0 };
  }, [summary, donutCategory, stockCounts]);

  const todayMetrics = useMemo(() => {
    return summary?.todayMetrics || { todayInCount: 0, todayInQty: 0, todayOutCount: 0, todayOutQty: 0 };
  }, [summary]);

  const topOutProducts30d = useMemo(() => {
    return (summary?.topOutProducts30d || [])
      .filter((row) => getKategoriFromKode(row?.kode) === "PRODUK UTAMA")
      .slice(0, 5);
  }, [summary]);
  const last6MonthsTrend = useMemo(() => summary?.last6MonthsTrend || [], [summary]);
  
  const needsAttention = Number(stockCounts.habis || 0) > 0 || Number(stockCounts.menipis || 0) > 0;

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Dashboard</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Ringkasan inventaris dan transaksi operasional hari ini.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-[13px] font-medium text-zinc-500 bg-white border border-zinc-200 px-3 py-1.5 rounded-md shadow-sm">
            {todayLocalISO()}
          </div>
          <button
            type="button"
            onClick={fetchSummary}
            disabled={loading}
            className="inline-flex h-8 items-center justify-center gap-2 rounded-md bg-zinc-900 px-3 text-[13px] font-medium text-white shadow-sm transition hover:bg-zinc-800 focus:outline-none disabled:opacity-50"
          >
            <RefreshCcw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
            Refresh
          </button>
        </div>
      </header>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[13px] text-red-600">
          {error}
        </div>
      ) : null}

      {needsAttention ? (
        <div className="flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 p-4">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="text-[13px] text-amber-900">
            <span className="font-semibold">Perhatian:</span> {stockCounts.habis} produk habis dan {stockCounts.menipis} produk sedang menipis.
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard icon={Package} title="Total Produk" value={totalProduk} hint="Master produk aktif" />
        <KpiCard icon={ShoppingCart} title="Total Transaksi" value={ordersCount == null ? "-" : ordersCount} hint="Pesanan tercatat" />
        <KpiCard icon={ShieldCheck} title="Stok Aman" value={stockCounts.aman} hint={`Di atas batas (> ${safeThreshold})`} />
        <KpiCard icon={AlertOctagon} title="Stok Habis" value={stockCounts.habis} hint="Produk dengan stok 0" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        
        {/* Left Column */}
        <div className="space-y-6">
          <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-zinc-400" />
                Demand vs Restock (6 Bulan)
              </h2>
              <div className="flex items-center gap-4 text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-zinc-900"></span>Keluar</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-zinc-300"></span>Masuk</span>
              </div>
            </div>
            <div className="h-64">
              <MinimalLineChart rows={last6MonthsTrend} />
            </div>
          </section>

          <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                <Activity className="h-4 w-4 text-zinc-400" />
                Top Produk Utama Keluar (30 Hari)
              </h2>
              <Link to="/stock-out" className="text-[13px] font-medium text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 transition-colors">
                Lihat Semua <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            
            {topOutProducts30d.length === 0 ? (
              <div className="py-8 text-center text-[13px] text-zinc-500 border border-dashed border-zinc-200 rounded-md">
                Belum ada transaksi produk utama keluar.
              </div>
            ) : (
              <div className="space-y-4">
                {topOutProducts30d.map((r, i) => (
                  <div key={r.kode} className="flex items-center gap-4">
                    <div className="w-4 text-xs font-medium text-zinc-400">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-zinc-900 truncate" title={r.name || r.kode}>{r.name || r.kode}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{r.kode}</div>
                    </div>
                    <div className="text-[13px] font-semibold text-zinc-900 tabular-nums">{r.qty}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-zinc-900 mb-5 flex items-center gap-2">
              <ArrowLeftRight className="h-4 w-4 text-zinc-400" />
              Transaksi Hari Ini
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-md border border-zinc-100 bg-zinc-50">
                <div>
                  <div className="text-[13px] font-medium text-zinc-900">Stock Keluar</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{todayMetrics.todayOutQty} items demand</div>
                </div>
                <div className="text-sm font-semibold text-zinc-900 tabular-nums">{todayMetrics.todayOutCount} trx</div>
              </div>
              <div className="flex items-center justify-between p-3 rounded-md border border-zinc-100 bg-zinc-50">
                <div>
                  <div className="text-[13px] font-medium text-zinc-900">Stock Masuk</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">{todayMetrics.todayInQty} items restock</div>
                </div>
                <div className="text-sm font-semibold text-zinc-900 tabular-nums">{todayMetrics.todayInCount} trx</div>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-zinc-900 mb-4 flex items-center gap-2">
              <PieChart className="h-4 w-4 text-zinc-400" />
              Distribusi Stok
            </h2>
            <select
              value={donutCategory}
              onChange={(e) => setDonutCategory(e.target.value)}
              className="w-full mb-6 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[13px] text-zinc-900 shadow-sm focus:border-zinc-900 focus:outline-none focus:ring-1 focus:ring-zinc-900"
            >
              {DONUT_CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            
            <div className="flex justify-center mb-6">
              <MinimalDonut
                segments={[
                  { label: "Aman", value: donutStockCounts.aman, className: "stroke-zinc-900" },
                  { label: "Menipis", value: donutStockCounts.menipis, className: "stroke-zinc-400" },
                  { label: "Habis", value: donutStockCounts.habis, className: "stroke-zinc-200" },
                ]}
                centerLabel={`${donutTotal}`}
                centerSub="PRODUK"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[13px]">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-zinc-900"></span><span className="text-zinc-600">Aman</span></div>
                <div className="font-semibold text-zinc-900 tabular-nums">{donutStockCounts.aman}</div>
              </div>
              <div className="flex items-center justify-between text-[13px]">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-zinc-400"></span><span className="text-zinc-600">Menipis</span></div>
                <div className="font-semibold text-zinc-900 tabular-nums">{donutStockCounts.menipis}</div>
              </div>
              <div className="flex items-center justify-between text-[13px]">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-zinc-200"></span><span className="text-zinc-600">Habis</span></div>
                <div className="font-semibold text-zinc-900 tabular-nums">{donutStockCounts.habis}</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, hint, icon: Icon }) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm flex flex-col justify-between group">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-medium text-zinc-500 group-hover:text-zinc-700 transition-colors">{title}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-colors" strokeWidth={2} />}
      </div>
      <div className="mt-3 text-3xl font-semibold tracking-tight text-zinc-900 tabular-nums">{value}</div>
      {hint && <div className="mt-3 text-[11px] text-zinc-400">{hint}</div>}
    </div>
  );
}

function MinimalDonut({ segments, centerLabel, centerSub }) {
  const size = 160;
  const strokeWidth = 12;
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const total = (segments || []).reduce((sum, s) => sum + (Number(s?.value) || 0), 0);

  let offset = 0;
  const normalized = (segments || []).map((s) => {
    const v = Math.max(0, Number(s?.value) || 0);
    const frac = total > 0 ? v / total : 0;
    const len = frac * c;
    const o = offset;
    offset += len;
    return { ...s, v, len, offset: o };
  });

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
        <circle cx={size / 2} cy={size / 2} r={r} className="fill-none stroke-zinc-100" strokeWidth={strokeWidth} />
        {normalized.map((s) => (
          <circle
            key={s.label}
            cx={size / 2}
            cy={size / 2}
            r={r}
            className={`fill-none ${s.className || "stroke-zinc-300"} transition-all duration-700 ease-out`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${s.len} ${c - s.len}`}
            strokeDashoffset={-s.offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        ))}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-bold tracking-tight text-zinc-900 tabular-nums">{centerLabel}</div>
        <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mt-0.5">{centerSub}</div>
      </div>
    </div>
  );
}

function MinimalLineChart({ rows }) {
  const w = 700;
  const h = 240;
  const padX = 20;
  const padY = 20;

  const values = (rows || []).flatMap((r) => [Number(r?.masuk) || 0, Number(r?.keluar) || 0]);
  const maxY = Math.max(1, ...values);

  const xForIndex = (i) => {
    const n = Math.max(1, (rows || []).length - 1);
    return padX + (i / n) * (w - padX * 2);
  };

  const yForValue = (v) => {
    const t = (Number(v) || 0) / maxY;
    return h - padY - t * (h - padY * 2);
  };

  const poly = (kind) => {
    return (rows || [])
      .map((r, i) => {
        const v = kind === "masuk" ? r.masuk : r.keluar;
        return `${xForIndex(i)},${yForValue(v)}`;
      })
      .join(" ");
  };

  return (
    <div className="w-full h-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full overflow-visible">
        {/* Horizontal Grid lines */}
        {[0, 0.5, 1].map((pct) => (
          <line key={pct} x1={padX} y1={padY + pct * (h - padY * 2)} x2={w - padX} y2={padY + pct * (h - padY * 2)} className="stroke-zinc-100" strokeWidth="1" />
        ))}
        
        {/* Lines */}
        <polyline points={poly("masuk")} className="fill-none stroke-zinc-300" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <polyline points={poly("keluar")} className="fill-none stroke-zinc-900" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* X Axis Labels */}
        {(rows || []).map((r, i) => {
          const x = xForIndex(i);
          return (
            <text key={r.key} x={x} y={h} textAnchor="middle" className="fill-zinc-400 font-medium tracking-wide" style={{ fontSize: 10 }}>
              {r.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export default Dashboard;
